import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { formatAUD } from "../data/pricing";
import { formatAUDexact } from "../data/signsPricing";
import { createOrder } from "../lib/api";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Store, Check } from "lucide-react";

const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

function joinAddress(a) {
  const line = [a.line1, a.line2].filter(Boolean).join(", ");
  const city = [a.suburb, a.state, a.postcode].filter(Boolean).join(" ");
  return [line, city, a.country].filter(Boolean).join(", ");
}

const EMPTY_ADDRESS = { line1: "", line2: "", suburb: "", state: "NSW", postcode: "", country: "Australia" };

export default function Checkout() {
  const { items, totals, clearCart } = useCart();
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "",
    email: "", phone: "",
    billing: { ...EMPTY_ADDRESS },
    shipping: { ...EMPTY_ADDRESS },
    delivery_method: null, artwork_consent: false,
    same_as_billing: true, notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateAddr = (which, k, v) => setForm((f) => ({ ...f, [which]: { ...f[which], [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.delivery_method) { toast.error("Please choose delivery or pickup"); return; }
    if (!form.artwork_consent) { toast.error("Please tick the artwork approval box to place your order"); return; }
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    setSubmitting(true);
    try {
      const isPickup = form.delivery_method === "pickup";
      const deliveryStr = joinAddress(form.shipping);
      const billingStr = isPickup || form.same_as_billing ? deliveryStr : joinAddress(form.billing);
      const shippingStr = isPickup ? "Pickup — Kwik Kopy Shellharbour" : deliveryStr;
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        business_name: form.business_name,
        email: form.email,
        phone: form.phone,
        billing_address: billingStr,
        shipping_address: shippingStr,
        local_pickup: isPickup,
        artwork_consent: form.artwork_consent,
        notes: form.notes,
        items: items.map((i) => ({
          product_type: i.product_type,
          product_id: i.product_id,
          product_name: i.product_name,
          design_preview: null,
          shape: i.shape || null,
          size: i.size,
          quantity: i.quantity,
          unit_price: i.unit_price,
          line_total: i.line_total,
          artwork_file_id: i.artwork_file_id || null,
          print_sides: i.print_sides || null,
          cutting: i.cutting || null,
          eyelets: i.eyelets ?? null,
          needs_quote: !!i.needs_quote,
          width_mm: i.width_mm ?? null,
          height_mm: i.height_mm ?? null,
          qty_per_design: i.qty_per_design ?? null,
          kinds: i.kinds ?? null,
          artwork_files: i.artwork_files || null,
          artwork_mode: i.artwork_mode || null,
          copyright_confirmed: i.copyright_confirmed ?? null,
        })),
        subtotal: totals.subtotal,
        gst: totals.gst,
        total: totals.total,
      };
      const res = await createOrder(payload);
      clearCart();
      navigate(`/order/${res.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed, please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto p-14 text-center">
          <h1 className="font-display text-3xl font-black">Your cart is empty</h1>
          <Link to="/stickers" className="btn-primary mt-6 inline-flex">Start shopping</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-16">
        <div className="eyebrow">Checkout</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2">Almost there.</h1>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <div className="sf-card p-6 space-y-4">
              <div className="eyebrow">Contact</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name" required value={form.first_name} onChange={(v) => update("first_name", v)} testid="checkout-first-name" />
                <Field label="Last name" required value={form.last_name} onChange={(v) => update("last_name", v)} testid="checkout-last-name" />
                <Field label="Business name (optional)" value={form.business_name} onChange={(v) => update("business_name", v)} testid="checkout-business" />
                <Field label="Phone" required value={form.phone} onChange={(v) => update("phone", v)} testid="checkout-phone" />
                <Field label="Email" required type="email" value={form.email} onChange={(v) => update("email", v)} testid="checkout-email" className="sm:col-span-2" />
              </div>
            </div>

            <div className="sf-card p-6 space-y-4" data-testid="checkout-delivery-method">
              <div className="eyebrow">Delivery or pickup</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MethodButton active={form.delivery_method === "delivery"} onClick={() => update("delivery_method", "delivery")} icon={Truck} title="Delivery" sub="Delivered to your address. Cost confirmed after checkout, based on weight and destination." testid="checkout-method-delivery" />
                <MethodButton active={form.delivery_method === "pickup"} onClick={() => update("delivery_method", "pickup")} icon={Store} title="Pickup" sub="Collect from Kwik Kopy Shellharbour. We'll let you know when it's ready." testid="checkout-method-pickup" />
              </div>
            </div>

            <div className="sf-card p-6 space-y-5">
              <div className="eyebrow" data-testid="checkout-address-heading">{form.delivery_method === "pickup" ? "Billing address" : "Delivery address"}</div>
              <AddressBlock
                prefix="shipping"
                value={form.shipping}
                onChange={(k, v) => updateAddr("shipping", k, v)}
                required
              />
              {form.delivery_method !== "pickup" && (
                <>
                  <label className="inline-flex items-center gap-2 text-sm pt-1">
                    <input type="checkbox" checked={form.same_as_billing} onChange={(e) => update("same_as_billing", e.target.checked)} data-testid="checkout-same-as-billing"/>
                    Billing address same as delivery
                  </label>
                  {!form.same_as_billing && (
                    <div className="pt-3 border-t border-dashed border-[#E2DDD5] space-y-5">
                      <div className="eyebrow">Billing address</div>
                      <AddressBlock
                        prefix="billing"
                        value={form.billing}
                        onChange={(k, v) => updateAddr("billing", k, v)}
                        required
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="sf-card p-6 space-y-4">
              <div className="eyebrow">Notes</div>
              <TextArea label="Order notes (optional)" value={form.notes} onChange={(v) => update("notes", v)} testid="checkout-notes" />
            </div>

            <label
              className={`flex gap-4 items-start p-6 rounded-2xl border-2 cursor-pointer transition-colors ${form.artwork_consent ? "border-[#10B981] bg-[#10B981]/10" : "border-[color:var(--brand)] bg-white"}`}
              data-testid="checkout-artwork-approval"
            >
              <input type="checkbox" className="sr-only" checked={form.artwork_consent} onChange={(e) => update("artwork_consent", e.target.checked)} data-testid="checkout-artwork-consent"/>
              <span className={`h-8 w-8 rounded-lg border-2 flex-shrink-0 flex items-center justify-center transition-colors ${form.artwork_consent ? "bg-[#10B981] border-[#10B981] text-white" : "border-[color:var(--brand)] bg-white"}`} aria-hidden="true">
                {form.artwork_consent && <Check size={20} strokeWidth={3} />}
              </span>
              <span>
                <span className="block eyebrow mb-1">Artwork approval — required</span>
                <span className="block font-display font-bold text-lg leading-snug">I confirm that I own this artwork or have permission from the copyright owner to reproduce and print it.</span>
                <span className="block text-xs text-slate-500 mt-2">Every order is confirmed with you before printing starts.</span>
              </span>
            </label>

            <div className="sf-card p-6 bg-[#FFF4F0] !border-[#FFD9CC]">
              <div className="eyebrow">Payment</div>
              <p className="mt-2 text-sm text-slate-700">We'll contact you to finalise payment after you place your order. Online card payment is coming soon — your order is reserved either way.</p>
            </div>

            <button disabled={submitting} className="btn-primary w-full" data-testid="checkout-submit">
              {submitting ? "Placing order…" : `Place order · ${formatAUDexact(totals.total)}${form.delivery_method === "delivery" ? " + delivery" : ""}`}
            </button>
          </div>

          <aside className="lg:col-span-4">
            <div className="sf-card p-6 sticky top-20">
              <div className="eyebrow mb-3">Your order</div>
              <div className="space-y-3 max-h-80 overflow-auto">
                {items.map((i) => (
                  <div key={i.cart_id} className="flex justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{i.product_name}</div>
                      <div className="text-slate-500 text-xs">
                        {i.shape ? `${i.shape} · ` : ""}{i.size} · {i.quantity.toLocaleString()}
                        {i.print_sides && <> · {i.print_sides === "double" ? "DS" : "SS"}</>}
                      </div>
                    </div>
                    <div className="price-tick font-semibold">{i.needs_quote ? <span className="text-[#003DA5] text-sm font-bold tracking-wide uppercase">Quote</span> : formatAUDexact(i.line_total)}</div>
                  </div>
                ))}
              </div>
              <div className="h-px bg-[#E2DDD5] my-4"/>
              <div className="space-y-1 text-sm">
                <Row label="Subtotal" value={formatAUDexact(totals.subtotal)} />
                <Row label="GST (10%)" value={formatAUDexact(totals.gst)} />
                <Row label="Delivery" value={form.delivery_method === "pickup" ? "Pickup" : form.delivery_method === "delivery" ? "To be confirmed" : "Choose above"} />
              </div>
              <div className="h-px bg-[#E2DDD5] my-3"/>
              <div className="flex justify-between items-baseline">
                <div className="font-display font-bold">Total</div>
                <div className="font-display font-black text-2xl price-tick">{formatAUDexact(totals.total)}</div>
              </div>
            </div>
          </aside>
        </form>
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "", placeholder = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
      <input required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none"
        data-testid={testid}/>
    </label>
  );
}
function TextArea({ label, value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
      <textarea required={required} value={value} onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none resize-none"
        data-testid={testid}/>
    </label>
  );
}
function MethodButton({ active, onClick, icon: Icon, title, sub, testid }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} data-testid={testid}
      className={`rounded-2xl border-2 p-5 text-left flex gap-4 items-start transition-colors ${active ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-[#E2DDD5] bg-white hover:border-[color:var(--brand)]"}`}>
      <Icon size={28} style={{ color: "var(--brand)" }} className="flex-shrink-0" />
      <span>
        <span className="block font-display font-black text-xl">{title}</span>
        <span className="block text-xs text-slate-500 mt-1">{sub}</span>
      </span>
    </button>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-semibold price-tick">{value}</span></div>;
}

function AddressBlock({ prefix, value, onChange, required }) {
  const set = (k) => (v) => onChange(k, v);
  return (
    <div className="space-y-4">
      <Field
        label="Street address"
        placeholder="123 Example Street"
        value={value.line1}
        onChange={set("line1")}
        required={required}
        testid={`checkout-${prefix}-line1`}
      />
      <Field
        label="Apartment, suite, unit (optional)"
        placeholder="Unit 4"
        value={value.line2}
        onChange={set("line2")}
        testid={`checkout-${prefix}-line2`}
      />
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
        <Field
          label="Suburb"
          placeholder="Shellharbour"
          value={value.suburb}
          onChange={set("suburb")}
          required={required}
          testid={`checkout-${prefix}-suburb`}
          className="sm:col-span-3"
        />
        <label className="block sm:col-span-2">
          <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">State</span>
          <select
            required={required}
            value={value.state}
            onChange={(e) => set("state")(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none"
            data-testid={`checkout-${prefix}-state`}
          >
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <Field
          label="Postcode"
          placeholder="2529"
          value={value.postcode}
          onChange={(v) => set("postcode")(v.replace(/\D/g, "").slice(0, 4))}
          required={required}
          testid={`checkout-${prefix}-postcode`}
          className="sm:col-span-1"
        />
      </div>
      <Field
        label="Country"
        value={value.country}
        onChange={set("country")}
        required={required}
        testid={`checkout-${prefix}-country`}
      />
    </div>
  );
}
